<script lang="ts">
  import type { PowerUpKind } from '../../shared/power-ups';
  import { POWER_UP_INFO } from '../content/power-ups';
  import { powerUpTileStyle } from './chaos';

  /** One letter tile's presentation; hit testing is geometric, so the tile owns no input. */
  interface Props {
    index: number;
    letter: string;
    revealed: boolean;
    revealing: boolean;
    selected: boolean;
    head: boolean;
    /** Chaos: a live spawn sits here; the theme's per-kind treatment restyles the tile. */
    powerUp?: PowerUpKind | undefined;
    /** Chaos: hidden by fog unless part of the trace. */
    fogged?: boolean;
  }
  let {
    index,
    letter,
    revealed,
    revealing,
    selected,
    head,
    powerUp = undefined,
    fogged = false,
  }: Props = $props();
</script>

<div
  class="tile"
  class:qu={letter === 'qu'}
  class:hidden-letter={!revealed}
  class:sel={selected}
  class:head
  class:revealing
  class:fogged={fogged && !selected}
  class:boosted={powerUp !== undefined}
  style:--tile-index={index}
  data-index={index}
  data-power-up={powerUp}
  title={powerUp ? POWER_UP_INFO[powerUp].title : undefined}
  style={powerUp ? powerUpTileStyle(powerUp) : undefined}
>
  {revealed ? (letter === 'qu' ? 'Qu' : letter.toUpperCase()) : ''}
</div>

<style>
  .tile.revealing:not(.sel) {
    animation: var(--theme-reveal-animation);
    /* Delay is decorative and never controls when letters or input become available. */
    animation-delay: calc(var(--tile-index) * 12ms);
  }
  .tile.sel {
    background: var(--theme-tile-texture), var(--theme-tile-selected);
    background-size: var(--theme-tile-texture-size), auto;
    color: var(--theme-tile-selected-text);
    transform: var(--theme-tile-select-transform);
    box-shadow: var(--theme-tile-selected-shadow);
    animation: var(--theme-select-animation);
  }
  .tile.head {
    background: var(--theme-tile-texture), var(--theme-tile-head);
    background-size: var(--theme-tile-texture-size), auto;
    color: var(--theme-tile-head-text);
    outline: var(--theme-tile-head-outline);
    outline-offset: -4px;
    box-shadow: var(--theme-tile-head-shadow);
  }
  .tile.hidden-letter {
    color: transparent;
    background: var(--theme-surface);
    border: var(--theme-tile-border);
    box-shadow: var(--theme-tile-shadow);
  }
  .tile.hidden-letter::after {
    content: '?';
    position: absolute;
    color: var(--theme-surface-text);
    font-size: 0.55em;
    opacity: 0.75;
  }
  /* Chaos: the theme's per-kind treatment (`--theme-power-up-<kind>-*`) restyles the whole tile;
     a selected tile returns to the pack's selected/head look. */
  .tile.boosted:not(.sel) {
    background: var(--pu-background);
    background-size: var(--pu-background-size);
    color: var(--pu-text);
    border: var(--pu-border);
    box-shadow: var(--pu-shadow);
    transform: var(--pu-transform);
    animation: var(--pu-animation);
  }
  /* The glyph is decoration: tile text stays the letter alone for assistive tech and tests. */
  .tile.boosted::before {
    content: var(--pu-glyph);
    position: absolute;
    top: 2px;
    right: 4px;
    font-size: 0.4em;
    line-height: 1;
    color: var(--pu-glyph-color);
    pointer-events: none;
  }
  .tile.fogged {
    color: transparent;
  }
  .tile.fogged::after {
    content: '·';
    position: absolute;
    color: var(--theme-tile-text);
    opacity: 0.5;
  }
</style>
